import { FC, useMemo } from 'react';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import multicreationUnavailableBannerButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_unavailable_banner_button_click';
import multicreationUnavailableBannerShown from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_unavailable_banner_shown';
import { Banner, Button, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';

import Conversion from 'bloko/blocks/conversion';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import {
    NotAvailableForCreationModal,
    NotAvailableVacancy,
} from 'components/MultiCreationAuction/VacanciesTable/NotAvailableForCreationModal/NotAvailableForCreationModal';

import { useSelector } from 'store/utils';

const TrlKeys = {
    text: 'MultiCreationAuction.notAvailableForCreationBanner.text',
    button: 'MultiCreationAuction.notAvailableForCreationBanner.button',
    vacancies: {
        zero: 'MultiCreationAuction.notAvailableForCreationBanner.vacancies.zero',
        one: 'MultiCreationAuction.notAvailableForCreationBanner.vacancies.one',
        some: 'MultiCreationAuction.notAvailableForCreationBanner.vacancies.some',
        many: 'MultiCreationAuction.notAvailableForCreationBanner.vacancies.many',
    },
};

const COMMA_SEPARATED_NUMBER_REGEX = /(?:^|,)\s*(\d+)\s*(?=,|$)/g;

export const NotAvailableForCreationBanner: FC = () => {
    const vacancySearchValue = useSelector(({ multiCreation }) => multiCreation.vacancySearchValue);
    const activeVacancies = useSelector(({ multiCreation }) => multiCreation.vacancies);
    const notAvailableForCreationVacancies = useSelector(
        ({ multiCreation }) => multiCreation.notAvailableForCreationVacancies
    );
    const [isModalVisible, setIsModalVisibleOn, setIsModalVisibleOff] = useOnOffState(false);

    const { isMobile } = useBreakpoint();
    const trls = useTrls();

    const notAvailableVacancies: NotAvailableVacancy[] = useMemo(() => {
        if (vacancySearchValue) {
            const vacancySearchValueParsed = (vacancySearchValue.match(COMMA_SEPARATED_NUMBER_REGEX) || []).map(
                (match) => Number(match.replace(/[^\d]/g, ''))
            );
            const notAvailableBySearchVacanciesIds = vacancySearchValueParsed.filter(
                (vacancyId) => !activeVacancies.some((vacancy) => vacancy.vacancyId === vacancyId)
            );
            return notAvailableBySearchVacanciesIds.map((vacancyId) => {
                const foundVacancy = notAvailableForCreationVacancies.find(
                    (vacancy) => vacancy.vacancyId === vacancyId
                );
                return {
                    vacancyId,
                    name: foundVacancy?.name || '',
                    areaName: foundVacancy?.areaName || '',
                    auctionCampaignCreationStatus: foundVacancy?.auctionCampaignCreationStatus,
                    campaignAuctionStatus: foundVacancy?.campaignAuctionStatus,
                    isUnknownVacancy: !foundVacancy,
                };
            });
        }

        return notAvailableForCreationVacancies.map(
            ({ vacancyId, name, areaName, auctionCampaignCreationStatus, campaignAuctionStatus }) => {
                return {
                    vacancyId,
                    name,
                    areaName,
                    auctionCampaignCreationStatus,
                    campaignAuctionStatus,
                    isUnknownVacancy: false,
                };
            }
        );
    }, [vacancySearchValue, notAvailableForCreationVacancies, activeVacancies]);

    const vacanciesCount = notAvailableVacancies.length;

    if (!vacanciesCount) {
        return null;
    }

    return (
        <ElementShownAnchor fn={multicreationUnavailableBannerShown}>
            <Banner
                style="warning-secondary"
                size="small"
                layout={isMobile ? 'vertical' : 'horizontal'}
                showClose={false}
                content={
                    <Text typography="paragraph-2-regular">
                        {trls(TrlKeys.text)}{' '}
                        <Conversion
                            zero={trls(TrlKeys.vacancies.zero)}
                            one={trls(TrlKeys.vacancies.one)}
                            some={trls(TrlKeys.vacancies.some)}
                            many={trls(TrlKeys.vacancies.many)}
                            value={vacanciesCount}
                        />
                    </Text>
                }
                buttonBase={
                    <Button
                        mode="primary"
                        style="neutral"
                        size="small"
                        onClick={() => {
                            multicreationUnavailableBannerButtonClick();
                            setIsModalVisibleOn();
                        }}
                    >
                        {trls(TrlKeys.button)}
                    </Button>
                }
            />
            <VSpacing default={24} />
            <NotAvailableForCreationModal
                hideModal={setIsModalVisibleOff}
                isVisible={isModalVisible}
                notAvailableVacancies={notAvailableVacancies}
            />
        </ElementShownAnchor>
    );
};
